/** Business default for trip civil date+time → instant (scheduling / ETA). */
export const DEFAULT_TRIP_TIME_ZONE = 'Australia/Sydney';

export type DirectionsRoutingPreference =
  | 'TRAFFIC_UNAWARE'
  | 'TRAFFIC_AWARE'
  | 'TRAFFIC_AWARE_OPTIMAL';

export type RouteDriveSourceResolved = 'directions' | 'static' | 'static_fallback' | 'unavailable';

/**
 * Offset (ms) of `timeZone` relative to UTC at the given instant:
 * localWall = utcInstant + offset.
 */
function timeZoneOffsetMs(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);

  const map: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }

  const asUtc = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second)
  );
  return asUtc - instant.getTime();
}

/**
 * Convert a civil date + pickup time in `timeZone` to an ISO-8601 UTC instant.
 */
export function buildTripDepartureIso(
  dateYmd: string,
  pickupTime: string,
  timeZone: string = DEFAULT_TRIP_TIME_ZONE
): string {
  const time = pickupTime.length === 5 ? `${pickupTime}:00` : pickupTime.slice(0, 8);
  const [y, m, d] = dateYmd.split('-').map(Number);
  const [hh, mm, ss = 0] = time.split(':').map(Number);

  let utc = new Date(Date.UTC(y, m - 1, d, hh, mm, Math.floor(ss)));
  const offset1 = timeZoneOffsetMs(utc, timeZone);
  utc = new Date(utc.getTime() - offset1);
  const offset2 = timeZoneOffsetMs(utc, timeZone);
  if (offset2 !== offset1) {
    utc = new Date(Date.UTC(y, m - 1, d, hh, mm, Math.floor(ss)) - offset2);
  }
  return utc.toISOString();
}

/**
 * Google Routes: TRAFFIC_AWARE requires a future departureTime.
 * Past / missing → TRAFFIC_UNAWARE (typical duration, no departure).
 */
export function classifyDirectionsRouting(
  departureTimeIso: string | null | undefined,
  now: Date = new Date()
): {
  routingPreference: DirectionsRoutingPreference;
  departureTimeIso?: string;
} {
  if (!departureTimeIso) {
    return { routingPreference: 'TRAFFIC_UNAWARE' };
  }
  const dep = new Date(departureTimeIso);
  if (!Number.isFinite(dep.getTime()) || dep.getTime() <= now.getTime()) {
    return { routingPreference: 'TRAFFIC_UNAWARE' };
  }
  return {
    routingPreference: 'TRAFFIC_AWARE',
    departureTimeIso,
  };
}

/** Map settings mode + Google success → persisted route_drive_source. */
export function resolveRouteDriveSourceAfterAttempt(args: {
  mode: 'static' | 'directions';
  googleOk: boolean;
}): RouteDriveSourceResolved {
  if (args.mode === 'static') return 'static';
  return args.googleOk ? 'directions' : 'static_fallback';
}
